import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import authAPI from '../../utils/api/auth';
import config from '../../config/config';
import { t } from '../../i18n';
import { translateBackendMessage } from '../../utils/translateMessage';

// Helper function to normalize error payloads to strings
const normalizeError = (payload) => {
  if (typeof payload === 'string') {
    return payload;
  }
  if (payload && typeof payload === 'object') {
    return payload.message || payload.error || JSON.stringify(payload);
  }
  return t('notifications.frontend.axios.unknownError');
};

// Async actions
export const login = createAsyncThunk(
  'api/auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      localStorage.setItem(config.APP_NAME + '_token', response.token);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const register = createAsyncThunk(
  'api/auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      localStorage.setItem(config.APP_NAME + '_token', response.token);
      return response;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const googleLogin = createAsyncThunk(
  'api/auth/googleLogin',
  async (token, { rejectWithValue }) => {
    try {
      const response = await authAPI.googleLogin(token);
      localStorage.setItem(config.TOKEN_KEY, response.data.token);
      localStorage.setItem(config.USER_KEY, JSON.stringify(response.data.user));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת התחברות עם Google');
    }
  }
);

export const changePassword = createAsyncThunk(
  'api/auth/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת שינוי סיסמה');
    }
  }
); 

export const resetPassword = createAsyncThunk(
  'api/auth/resetPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await authAPI.resetPassword(email);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'שגיאת איפוס סיסמה');
    }
  }
);  

export const logout = createAsyncThunk(
  '/logout',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      localStorage.removeItem(config.APP_NAME + '_token');
      dispatch(clearAuthState());
      return null;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem(config.APP_NAME + '_token'),
  loading: false,
  error: null
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuthState: (state) => {
      state.user = null;
      state.token = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        toast.success(t('notifications.frontend.authSlice.loginSuccess'), {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        const normalized = normalizeError(action.payload);
        state.error = normalizeError(action.payload);
        const translated = translateBackendMessage(normalized);
        toast.error(translated || normalized, {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        // state.token = action.payload.token;
        toast.success(t('notifications.frontend.authSlice.registerSuccess'), {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        const normalized = normalizeError(action.payload);
        state.error = normalizeError(action.payload);
        const translated = translateBackendMessage(normalized);
        toast.error(translated || normalized, {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        toast.info(t('notifications.frontend.authSlice.logoutInfo'), {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        toast.success(t('notifications.frontend.authSlice.googleLoginSuccess'), {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        const normalized = normalizeError(action.payload);
        state.error = normalizeError(action.payload);
        const translated = translateBackendMessage(normalized);
        toast.error(translated || normalized, {
          autoClose: config.TOAST_AUTO_CLOSE,
          position: 'top-left',
          rtl: true
        });
      });
  }
});

export const { clearError, clearAuthState } = authSlice.actions;

export default authSlice.reducer; 